import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from './firebase'; 

const VocationalTracker = ({ tenantId }) => {
  const [students, setStudents] = useState([]);
  const [vocationalBank, setVocationalBank] = useState({}); // Dynamically loaded per student
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Form State
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [logDate, setLogDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Data State: scores = { "Stocking Shelves": 4, "Greeting": 2 }
  const [scores, setScores] = useState({});
  const [notes, setNotes] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [existingLogId, setExistingLogId] = useState(null);

  // 1. Initial Load: Fetch Students (WITH THE IA FIX)
  useEffect(() => {
    let isMounted = true; 
    const fetchStudents = async () => {
      try {
        if (!auth?.currentUser) return;
        const userRef = doc(db, 'districts', tenantId, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) {
          if (isMounted) { setError("User profile not found."); setLoading(false); }
          return;
        }
        const profile = userSnap.data();

        const studentsRef = collection(db, 'districts', tenantId, 'students');
        let studentQuery;
        
        if (profile?.role === 'Case Manager') {
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", auth.currentUser.uid));
        } else if (profile?.role === 'Instructional Assistant') {
          // --- THE FIX: Tell the IA to only look for their assigned Case Manager's students ---
          studentQuery = query(studentsRef, where("assignedCaseManager", "==", profile.assignedCaseManager));
        } else if (profile?.role === 'ECE Recorder') {
          studentQuery = query(studentsRef, where("schoolId", "==", profile.schoolId));
        } else {
          studentQuery = query(studentsRef); 
        }
        
        const studentDocs = await getDocs(studentQuery);
        const loadedStudents = studentDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (isMounted) setStudents(loadedStudents.sort((a, b) => (a.name || a.studentName || '').localeCompare(b.name || b.studentName || '')));
      } catch (err) {
        console.error("Error fetching students:", err);
        if (isMounted) setError("Failed to load database.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStudents();
    return () => { isMounted = false; };
  }, [tenantId]);

  // 2. Fetch Specific Student's Custom Vocational Sites
  useEffect(() => {
    const fetchStudentVocational = async () => {
      if (!selectedStudentId) {
        setVocationalBank({});
        setSelectedSite('');
        setScores({});
        return;
      }
      try {
        const studentRef = doc(db, 'districts', tenantId, 'students', selectedStudentId);
        const snap = await getDoc(studentRef);
        if (snap.exists()) {
          setVocationalBank(snap.data().customVocational || {});
        } else {
          setVocationalBank({});
        }
      } catch (err) {
        console.error("Error fetching individualized vocational data:", err);
      }
    };
    fetchStudentVocational();
  }, [selectedStudentId, tenantId]);

  // 3. THE LISTENER: Check for existing data on the SELECTED DATE
  useEffect(() => {
    const checkForExistingLog = async () => {
      if (!selectedStudentId || !selectedSite || !logDate) {
        setExistingLogId(null);
        return;
      }

      setSaveMessage('Checking records...');
      try {
        const q = query(
          collection(db, 'districts', tenantId, 'students', selectedStudentId, 'vocationalLogs'),
          where('date', '==', logDate),
          where('siteName', '==', selectedSite)
        );
        const snap = await getDocs(q);

        if (!snap.empty) {
          const existingData = snap.docs[0].data();
          setScores(existingData.scores || {});
          setNotes(existingData.notes || '');
          setExistingLogId(snap.docs[0].id);
          setSaveMessage('');
        } else {
          // Initialize empty scores for the active tasks at this site
          const defaultScores = {};
          (vocationalBank[selectedSite] || []).forEach(task => { defaultScores[task] = null; });
          setScores(defaultScores);
          setNotes('');
          setExistingLogId(null);
          setSaveMessage('');
        }
      } catch (err) {
        console.error("Error checking existing logs:", err);
        setSaveMessage('');
      }
    };

    checkForExistingLog();
  }, [selectedStudentId, selectedSite, logDate, tenantId, vocationalBank]);

  const handleScoreChange = (task, value) => {
    setScores(prev => ({ ...prev, [task]: value }));
  };

  const handleSubmit = async () => {
    if (!selectedStudentId || !selectedSite || !logDate) {
      alert("Please ensure Student, Site, and Date are selected.");
      return;
    }
    
    // Validate all tasks have a score
    const currentTasks = vocationalBank[selectedSite] || [];
    const unratedTasks = currentTasks.filter(task => scores[task] === null || scores[task] === undefined);
    
    if (unratedTasks.length > 0) {
      alert("Please rate all tasks for this job site before saving.");
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      const logId = existingLogId || Date.now().toString(); 
      const logRef = doc(db, 'districts', tenantId, 'students', selectedStudentId, 'vocationalLogs', logId);
      
      await setDoc(logRef, {
        date: logDate,
        timestamp: new Date().toISOString(),
        siteName: selectedSite,
        scores: scores,
        notes: notes,
        recordedBy: auth.currentUser.uid
      });

      setSaveMessage('Vocational Log Saved Successfully!');
      setExistingLogId(logId); 

      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error("Error saving log:", error);
      setSaveMessage('Error saving data.');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>Loading Vocational Tracker...</div>;
  if (error) return <div style={{ padding: '20px', color: '#ba0c2f', fontFamily: 'sans-serif' }}>{error}</div>;

  const availableSites = Object.keys(vocationalBank || {});
  const activeTasks = selectedSite && vocationalBank[selectedSite] ? vocationalBank[selectedSite] : [];

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', fontFamily: 'sans-serif' }}>
      
      <div style={{ borderBottom: '2px solid #003366', paddingBottom: '10px', marginBottom: '25px' }}>
        <h2 style={{ margin: 0, color: '#003366', fontSize: '28px' }}>Vocational Tracker</h2>
        <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Community Readiness & Work-Study (Individualized)</p>
      </div>

      <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
          <div style={{ flex: '1 1 250px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Select Student</label>
            <select 
              value={selectedStudentId} 
              onChange={(e) => setSelectedStudentId(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px', backgroundColor: '#f9f9f9' }}
            >
              <option value="">-- Choose Student --</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.name || s.studentName || s.fullName || 'Unknown Student'}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Date</label>
            <input 
              type="date" 
              value={logDate}
              onChange={(e) => setLogDate(e.target.value)}
              style={{ width: '100%', padding: '9px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px', backgroundColor: '#f9f9f9', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

      {selectedStudentId && availableSites.length === 0 && (
        <div style={{ padding: '20px', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', border: '1px solid #ffeeba' }}>
          <strong>No Job Sites Found!</strong> The Case Manager needs to set up this student's individualized vocational sites in the <em>Caseload Manager</em> app before shifts can be tracked.
        </div>
      )}

      {selectedStudentId && availableSites.length > 0 && (
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#666', fontSize: '13px' }}>Assigned Job Site</label>
          <select 
            value={selectedSite} 
            onChange={(e) => setSelectedSite(e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '15px' }}
          >
            <option value="">-- Select Work Environment --</option>
            {availableSites.map((site, idx) => (
              <option key={idx} value={site}>{site}</option>
            ))}
          </select>
        </div>
      )}

      {selectedSite && activeTasks.length > 0 && (
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: '#ba0c2f' }}>Shift Data Collection</h3>
            {existingLogId ? (
              <span style={{ backgroundColor: '#e6f0fa', color: '#003366', padding: '5px 10px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>✏️ Viewing/Editing Saved Log</span>
            ) : (
              <span style={{ backgroundColor: '#f4f4f4', color: '#666', padding: '5px 10px', borderRadius: '15px', fontSize: '12px', fontWeight: 'bold' }}>📄 Creating New Log</span>
            )}
          </div>

          <p style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginBottom: '20px' }}>
            Rating Scale: 4 (Independent), 3 (Verbal/Visual), 2 (Gestural), 1 (Partial Physical), 0 (Full Physical)
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '25px' }}>
            {activeTasks.map((task, idx) => (
              <div key={idx} style={{ padding: '15px', backgroundColor: '#fcfcfc', border: '1px solid #eee', borderRadius: '8px' }}>
                <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '10px' }}>{task}</div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {[0, 1, 2, 3, 4].map(val => (
                    <button
                      key={val}
                      onClick={() => handleScoreChange(task, val)}
                      style={{
                        flex: '1 1 40px',
                        padding: '12px 0',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        border: '2px solid',
                        borderColor: scores[task] === val ? '#ba0c2f' : '#ccc',
                        backgroundColor: scores[task] === val ? '#ba0c2f' : '#fff',
                        color: scores[task] === val ? '#fff' : '#666',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.1s'
                      }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Shift Notes (Optional)</label>
            <textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Struggled with focusing during the first hour."
              style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ccc', minHeight: '80px', boxSizing: 'border-box', fontFamily: 'sans-serif' }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #eee', paddingTop: '20px' }}>
            <span style={{ color: saveMessage.includes('Error') ? '#ba0c2f' : (saveMessage === 'Checking records...' ? '#666' : '#28a745'), fontWeight: 'bold' }}>{saveMessage}</span>
            <button 
              onClick={handleSubmit}
              disabled={isSaving}
              style={{ padding: '12px 25px', backgroundColor: '#003366', color: '#fff', border: 'none', borderRadius: '6px', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '16px' }}
            >
              {isSaving ? 'Logging Shift...' : (existingLogId ? 'Update Shift' : 'Save Shift')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocationalTracker;